import { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateOnly } from '../../../common/utils/date';
import { useOpuBreakdown } from '../contexts/opu-breakdown';
import OpuSessionDetailModal from '../../opu/forms/session-detail-modal';

import '../../../common/styles/catalog.css';

// Estado final de una transferencia → badge legible (status + parto).
const STATUS = {
    pregnant:  { label: 'Preñada',   cls: 'cat-badge--2' },
    confirmed: { label: 'Confirmada', cls: 'cat-badge--2' },
    open:      { label: 'En curso',  cls: 'cat-badge--1' },
    empty:     { label: 'Vacía',     cls: 'cat-badge--0' },
    lost:      { label: 'Perdida',   cls: 'cat-badge--0' },
    born:      { label: 'Parto',     cls: 'cat-badge--2' },
};

// Desglose reproductivo OPU de la donadora (admin-animals.md · 0109): sesiones de aspiración +
// destino de sus embriones con el estado final (dx / parto / cría). Read-only, kpis:read.
export default function DonorOpuBreakdownPanel({ animalId }) {
    const { data, isLoading, error } = useOpuBreakdown(animalId);
    const [openSession, setOpenSession] = useState(null);

    const sessions = data?.sessions || [];
    const placements = data?.placements || [];

    if (error) {
        return (
            <section className="cat-detail__card">
                <div className="cat-detail__section-head"><h2 className="cat-detail__section-title">Desglose OPU</h2></div>
                <p className="cat-detail__empty">{error.status === 403 ? 'Sin permiso para ver el desglose OPU.' : 'No se pudo cargar el desglose OPU.'}</p>
            </section>
        );
    }
    if (isLoading) {
        return (
            <section className="cat-detail__card">
                <div className="cat-detail__section-head"><h2 className="cat-detail__section-title">Desglose OPU</h2></div>
                <p className="cat-detail__empty">Cargando…</p>
            </section>
        );
    }
    if (!sessions.length && !placements.length) return null; // no es donadora / sin actividad OPU

    return (
        <section className="cat-detail__card">
            <div className="cat-detail__section-head"><h2 className="cat-detail__section-title">Desglose OPU</h2></div>

            {/* Sesiones de aspiración */}
            {sessions.length > 0 && (
                <>
                    <h3 className="cat-detail__section-title" style={{ fontSize: '13px', marginBottom: '8px' }}>Sesiones de aspiración <span className="cat-detail__count">{sessions.length}</span></h3>
                    <table className="cat-table cat-table--compact">
                        <thead>
                            <tr><th>OPU</th><th>Fecha</th><th>Aspir.</th><th>Ovocitos</th><th>Viables</th><th>Embriones</th><th>Co-aspir.</th></tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => (
                                <tr key={s.opu_session_id} className="cat-table__row">
                                    <td>
                                        <button type="button" className="cat-table__name" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }} onClick={() => setOpenSession(s.opu_session_id)} title="Ver detalle de la sesión">
                                            OPU #{s.opu_session_id}
                                        </button>
                                    </td>
                                    <td>{formatDateOnly(s.date)}</td>
                                    <td>{s.aspirations ?? '—'}</td>
                                    <td>{s.oocytes ?? '—'}</td>
                                    <td>{s.viables ?? '—'}</td>
                                    <td>{s.embryos_produced ?? '—'}</td>
                                    <td>{s.session_donor_count ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Destino de los embriones (transferencias) */}
            {placements.length > 0 && (
                <>
                    <h3 className="cat-detail__section-title" style={{ fontSize: '13px', margin: '16px 0 8px' }}>Destino de embriones <span className="cat-detail__count">{placements.length}</span></h3>
                    <table className="cat-table cat-table--compact">
                        <thead>
                            <tr><th>Receptora</th><th>Finca</th><th>T.E.</th><th>Estado</th><th>Parto / cría</th></tr>
                        </thead>
                        <tbody>
                            {placements.map((p, i) => {
                                const r = p.recipient;
                                // Con cría → "Parida". Perdida → fase del dx negative. Resto → STATUS.
                                let st;
                                if (p.birth) st = { label: 'Parida', cls: 'cat-badge--2' };
                                else if (p.status === 'lost' || p.status === 'empty') {
                                    const neg = [p.dx1, p.dx2].find((d) => d?.result === 'negative');
                                    st = { label: neg?.number ? `Perdida DX${neg.number}` : 'Perdida', cls: 'cat-badge--0' };
                                } else st = STATUS[p.status] || { label: p.status || '—', cls: 'cat-badge--1' };
                                return (
                                    <tr key={p.embryo?.id ?? i} className="cat-table__row">
                                        <td className="cat-table__name">{r ? `${r.number ?? '—'}${r.name ? ` · ${r.name}` : ''}` : <span className="cat-table__empty">—</span>}</td>
                                        <td>{r?.farm?.name || <span className="cat-table__empty">—</span>}</td>
                                        <td>{p.te_date ? formatDateOnly(p.te_date) : <span className="cat-table__empty">—</span>}</td>
                                        <td><span className={`cat-badge ${st.cls}`}>{st.label}</span></td>
                                        <td>
                                            {p.birth
                                                ? <Link to={`/admin/animals/${p.birth.calf_id}`} className="cat-table__name">{p.birth.name || `#${p.birth.calf_id}`}{p.birth.date ? ` · ${formatDateOnly(p.birth.date)}` : ''}</Link>
                                                : <span className="cat-table__empty">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {openSession && <OpuSessionDetailModal sessionId={openSession} highlightDonorId={animalId} onClose={() => setOpenSession(null)} />}
        </section>
    );
}
